// Read-only CapEx Financials lens (portfolio). Project amounts and totals are already USD.
// Mounted only when the client policy gate passes; unmount drops all data.
// Presentation follows the drawer Financials tab (#9168); figures come from
// the authoritative server model (server/lib/projectHubFinancials.js).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { budgetHealth } from "../../../lib/projectDisplaySignals.js";
import { BudgetHealthLabel, budgetHealthColor } from "../ProjectDisplaySignals.jsx";
import { T } from "../../../app/tokens.js";
import { ProjectsTableScroll } from "../ProjectsScrollLayout.jsx";
import { getHubFinancials, exportHubFinancials, projectIdsKey } from "../../../data/projectHubClient.js";
import { Tip } from "../financials/DrawerFinancials.jsx";
import { formatCompact, formatExact, formatPct, isZeroMoney } from "../financials/drawerFinancialsModel.js";
import {
  portfolioFinancials, sortPortfolioRows, SORT_COLUMNS, DEFAULT_SORT, PORTFOLIO_CURRENCY as USD, coverageReason,
} from "./portfolioFinancialsModel.js";

const numFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

export function formatHubAmount(value) {
  if (value === null || value === undefined) return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  if (n === 0) return "0";
  return numFmt.format(n);
}

const eyebrow = { fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".07em", textTransform: "uppercase", color: T.mid };
const ghostBtn = { border: `1px solid ${T.border}`, background: T.card, color: T.dark, borderRadius: 8, padding: "4px 10px", fontSize: T.fsXs, fontWeight: T.fwSemibold, cursor: "pointer" };
const section = { border: `1px solid ${T.border}`, borderRadius: 12, background: T.card, padding: "14px 16px" };
const SOURCE_TIP = "Source: CapEx 2026 Working Doc (project record values).\nAll amounts USD, ex tax.\nTotals use Stage Active, financially tracked projects only and require complete per-metric coverage.\nVariance = Budget − Forecast. Remaining = Forecast − Actuals.";
const varColor = (budget, forecast) => budgetHealthColor(budgetHealth(budget, forecast));
const DASH = <span aria-label="Not available" style={{ color: T.light }}>—</span>;

function Money({ value, signed, testId, color = T.dark, weight, size, reason }) {
  if (value == null) {
    return reason
      ? <Tip as="span" align="right" testId={testId} content={reason}><span data-testid={testId ? `${testId}-value` : undefined} style={{ color: T.light }}>—</span></Tip>
      : <span data-testid={testId ? `${testId}-value` : undefined} style={{ color: T.light }}>—</span>;
  }
  return (
    <Tip as="span" align="right" testId={testId} content={formatExact(value, USD)}>
      <span data-testid={testId ? `${testId}-value` : undefined} style={{ color, fontWeight: weight, fontSize: size, fontVariantNumeric: "tabular-nums" }}>
        {formatCompact(value, USD, { signed })}
      </span>
    </Tip>
  );
}

export function PortfolioRatio({ r, overIsRed, testId }) {
  if (r == null || !Number.isFinite(r) || (overIsRed && budgetHealth(1, r).ratio == null)) return <Tip as="span" align="right" testId={`${testId}-info`} content="Percentage unavailable: requires a known numerator and a known, positive denominator. Zero is not a valid denominator."><span data-testid={testId} style={{ color: T.mid }}>{overIsRed ? "Not tracked" : "—"}</span></Tip>;
  const over = overIsRed && r > 1;
  const health = budgetHealth(1, overIsRed ? r : null);
  const color = overIsRed ? budgetHealthColor(health) : T.dark;
  return (
    <Tip as="span" align="right" testId={`${testId}-info`} content={`${overIsRed ? `${health.label}. ` : ""}Percentage before display rounding: ${r * 100}%. The drawing is capped at 100%; the value is not.`}>
    <span data-testid={testId} data-over={over ? "true" : "false"} style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "flex-end", color, fontWeight: over ? T.fwBold : T.fwSemibold }}>
      {r > 1 && Math.round(r * 100) === 100 ? ">100%" : formatPct(r)}
      <span aria-hidden="true" style={{ width: 52, height: 5, borderRadius: 3, background: T.lightGray, position: "relative", overflow: "hidden" }}>
        <span data-testid={testId ? `${testId}-fill` : undefined} data-scale={Math.max(0, Math.min(r, 1))} style={{ position: "absolute", inset: 0, transformOrigin: "left", transform: `scaleX(${Math.max(0, Math.min(r, 1))})`, background: overIsRed ? color : T.projectsHubNavy }} />
      </span>
    </span></Tip>
  );
}

export function ProjectsFinancials({ projectIds, canExport, refreshToken = 0, onCountChange, onOpenProject, projectHref }) {
  const idsKey = projectIdsKey(projectIds);
  const [state, setState] = useState({ key: null, data: null, error: null, loading: false });
  const [exportState, setExportState] = useState({ busy: false, error: null });
  const [reloadTick, setReloadTick] = useState(0);
  const exportContext = useRef({ mounted: true, idsKey, canExport });
  exportContext.current.idsKey = idsKey;
  exportContext.current.canExport = canExport;
  useEffect(() => {
    const context = exportContext.current;
    context.mounted = true;
    return () => { context.mounted = false; };
  }, []);

  useEffect(() => {
    const ids = idsKey ? idsKey.split(",") : [];
    let cancelled = false;
    setState({ key: idsKey, data: null, error: null, loading: ids.length > 0 });
    setExportState({ busy: false, error: null });
    if (ids.length === 0) return undefined;
    Promise.resolve()
      .then(() => getHubFinancials(ids))
      .then((data) => { if (!cancelled) setState({ key: idsKey, data, error: null, loading: false }); })
      .catch((err) => { if (!cancelled) setState({ key: idsKey, data: null, error: err?.message || "Could not load financials.", loading: false }); });
    return () => { cancelled = true; };
  }, [idsKey, reloadTick, refreshToken]);

  const data = state.key === idsKey ? state.data : null;
  const countCallback = useRef(onCountChange);
  countCallback.current = onCountChange;
  const renderedCount = Array.isArray(data?.rows) ? data.rows.length : 0;
  useEffect(() => { countCallback.current?.(renderedCount); }, [renderedCount]);
  exportContext.current.snapshotId = data?.snapshotId;
  const exportReady = Boolean(canExport && data?.snapshotId && state.key === idsKey);
  const fin = useMemo(() => (data ? portfolioFinancials(data) : null), [data]);

  async function handleExport() {
    if (!exportReady) return;
    setExportState({ busy: true, error: null });
    try {
      const blob = await exportHubFinancials(idsKey.split(","), data.snapshotId);
      if (!exportContext.current.mounted || !exportContext.current.canExport || exportContext.current.idsKey !== idsKey
        || exportContext.current.snapshotId !== data.snapshotId) return;
      if (typeof URL?.createObjectURL !== "function") throw new Error("This browser cannot download the workbook.");
      {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `project-financials-${data.snapshotId}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
      }
      setExportState({ busy: false, error: null });
    } catch (err) {
      if (!exportContext.current.mounted || exportContext.current.idsKey !== idsKey) return;
      setExportState({ busy: false, error: err?.message || "Export failed." });
    }
  }

  return (
    <div data-testid="projects-financials" className="pfin" style={{ display: "grid", gap: 12 }}>
      <style>{`
        .pfin .pfin-strip { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; }
        @media (max-width: 760px) { .pfin .pfin-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        .pfin .pfin-tr { cursor: pointer; }
        .pfin .pfin-tr:hover { background: ${T.hoverBg}; }
        .pfin .pfin-link { color: ${T.dark}; text-decoration: none; }
        .pfin .pfin-link:hover, .pfin .pfin-link:focus-visible { color: ${T.blue}; text-decoration: underline; }
        .pfin .pfin-sort { border: 0; background: none; padding: 0; font: inherit; color: inherit; letter-spacing: inherit; text-transform: inherit; cursor: pointer; }
      `}</style>
      <section style={section} aria-labelledby="pfin-title">
        <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <h3 id="pfin-title" style={{ margin: 0, display: "inline-flex", alignItems: "center", fontSize: T.fsXs, fontWeight: T.fwHubTitle, letterSpacing: ".05em", textTransform: "uppercase", color: T.projectsHubNavy }}>
            Portfolio financials
            <Tip label="About portfolio financials" testId="pfin-source-info" align="left" content={[SOURCE_TIP, data?.scope].filter(Boolean).join("\n")} />
          </h3>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: T.fsXs, color: T.mid, flexWrap: "wrap" }}>
            <span data-testid="projects-financials-scope" style={{ display: "inline-flex", alignItems: "center" }}>
              All values USD{fin ? ` · ${fin.trackedCount} of ${fin.activeCount} Active projects tracked` : ""}
              {fin && (
                <Tip label="About tracking coverage" testId="pfin-tracking-info" align="right"
                  content={[data?.coverageCaption, fin.untracked.length ? `Not financially tracked (n/a): ${fin.untracked.join(", ")}.` : "Every Active project in this selection is financially tracked."].filter(Boolean).join("\n")} />
              )}
            </span>
            <button type="button" data-testid="projects-financials-refresh" onClick={() => setReloadTick(value => value + 1)} style={ghostBtn}>Refresh</button>
            {canExport ? (
              <button
                type="button"
                data-testid="projects-financials-export"
                disabled={!exportReady || exportState.busy}
                onClick={handleExport}
                style={{ ...ghostBtn, color: exportReady ? T.dark : T.light, cursor: exportReady ? "pointer" : "not-allowed" }}
              >
                {exportState.busy ? "Preparing…" : "Download .xlsx"}
              </button>
            ) : null}
          </div>
        </header>
        {exportState.error && (
          <div role="alert" data-testid="projects-financials-export-error" style={{ marginBottom: 10, padding: "7px 10px", borderRadius: 8, color: T.red, fontSize: T.fsXs, background: T.redSoft }}>{exportState.error}</div>
        )}
        {projectIds.length === 0 ? (
          <div data-testid="projects-financials-empty" style={{ padding: "22px 14px", borderRadius: 10, border: `1px dashed ${T.borderDark}`, textAlign: "center", color: T.mid, fontSize: T.fsSm }}>No active projects in the current view.</div>
        ) : state.error && state.key === idsKey ? (
          <div role="alert" data-testid="projects-financials-error" style={{ padding: "10px 12px", borderRadius: 8, background: T.redSoft, color: T.red, fontSize: T.fsSm }}>
            {state.error}{" "}
            <button type="button" data-testid="projects-financials-retry" onClick={() => setReloadTick((n) => n + 1)} style={{ ...ghostBtn, marginLeft: 8 }}>Retry</button>
          </div>
        ) : !fin ? (
          <div data-testid="projects-financials-loading" aria-busy="true" className="pfin-strip">
            {[0, 1, 2, 3, 4].map((i) => <div key={i} style={{ height: 64, borderRadius: 10, background: T.lightGray }} />)}
          </div>
        ) : (
          <>
            <Summary fin={fin} />
            <PortfolioBar bar={fin.bar} />
          </>
        )}
      </section>
      {fin && projectIds.length > 0 && !(state.error && state.key === idsKey) && (
        <section style={section} aria-labelledby="pfin-byproject">
          <FinancialsTable fin={fin} coverageCaption={data.coverageCaption} onOpenProject={onOpenProject} projectHref={projectHref} />
        </section>
      )}
    </div>
  );
}

function Summary({ fin }) {
  const s = fin.summary; const t = fin.totals;
  const tiles = [
    ["budget", "Budget", s.budget, "approved", false, coverageReason("Budget", t.budget)],
    ["forecast", "Forecast", s.forecast, "at completion", false, coverageReason("Forecast", t.forecast)],
    ["variance", "Variance", s.variance, "budget − forecast", true, coverageReason("Variance", t.variance)],
    ["spent", "Spent (actuals)", s.spent, s.spentPct != null ? `${formatPct(s.spentPct)} of forecast` : "of forecast unavailable", false, coverageReason("Actuals", t.spent)],
    ["remaining", "Remaining", s.remaining, "forecast − actuals", false, "Requires complete Forecast and Actuals totals."],
  ];
  return (
    <div data-testid="pfin-summary" className="pfin-strip">
      {tiles.map(([key, label, value, sub, signed, reason]) => (
        <div key={key} data-testid={`pfin-tile-${key}`} style={{ minWidth: 0, padding: "10px 12px", borderRadius: 10, background: key === "budget" ? T.blueSoft : T.hoverBg, border: `1px solid ${T.border}` }}>
          <div style={{ ...eyebrow, display: "flex", alignItems: "center" }}>
            {label}
            {key === "spent" && <Tip label="About spent actuals" testId="pfin-spent-info" content="Actuals to date (spent_to_date) per the CapEx 2026 Working Doc project records, USD ex tax." />}
          </div>
          <div style={{ marginTop: 3 }}>
            <Money value={value} signed={signed} testId={`pfin-${key}`} size={T.fsLg} weight={T.fwExtrabold} reason={reason}
              color={key === "variance" && value != null && !isZeroMoney(value) && value < 0 && budgetHealth(s.budget, s.forecast).overage > 0 ? T.red : T.dark} />
          </div>
          <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 2 }}>{value == null ? "Incomplete coverage" : sub}</div>
        </div>
      ))}
    </div>
  );
}

const SEG_COLOR = { spent: T.projectsHubNavy, remaining: T.blue, headroom: T.greenDeep };

function PortfolioBar({ bar }) {
  if (bar.status !== "ok") {
    return <div data-testid="pfin-bar-unavailable" role="note" style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, border: `1px dashed ${T.borderDark}`, fontSize: T.fsXs, color: T.mid }}>{bar.reason}</div>;
  }
  const desc = bar.segments.map((g) => `${g.label} ${formatExact(g.value, USD)}`).concat(bar.overBudget != null ? [`Over budget ${formatExact(bar.overBudget, USD)}`] : []).join(", ");
  return (
    <figure data-testid="pfin-bar" style={{ margin: "12px 0 0" }}>
      <div role="img" aria-label={`Portfolio forecast composition: ${desc}`} style={{ position: "relative", display: "flex", height: 10, borderRadius: 5, overflow: "hidden", background: T.lightGray }}>
        {bar.segments.map((g) => g.share > 0 && <span key={g.key} data-testid={`pfin-seg-${g.key}`} data-share={g.share} style={{ width: `${g.share * 100}%`, background: SEG_COLOR[g.key] }} />)}
        {bar.overBudget != null && (
          <span data-testid="pfin-budget-marker" aria-hidden="true" style={{ position: "absolute", top: 0, bottom: 0, left: `${bar.budgetShare * 100}%`, width: 2, background: T.red }} />
        )}
      </div>
      <figcaption style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", marginTop: 7, fontSize: T.fsXs, color: T.mid }}>
        {bar.segments.map((g) => (
          <span key={g.key} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: SEG_COLOR[g.key] }} />
            {g.label} <Money value={g.value} testId={`pfin-legend-${g.key}`} weight={T.fwBold} />
          </span>
        ))}
        {bar.overBudget != null && (
          <span data-testid="pfin-over-budget" style={{ display: "inline-flex", alignItems: "center", gap: 5, color: T.red }}>
            <span aria-hidden="true" style={{ width: 2, height: 10, background: T.red }} />
            Over budget <Money value={bar.overBudget} testId="pfin-legend-over" color={T.red} weight={T.fwBold} />
            <span style={{ color: T.mid }}>(forecast beyond the budget line)</span>
          </span>
        )}
      </figcaption>
    </figure>
  );
}

const td = { padding: "9px 8px", fontSize: T.fsSm, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", borderBottom: `1px solid ${T.border}` };
const isModified = (e) => e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

function FinancialsTable({ fin, coverageCaption, onOpenProject, projectHref }) {
  const [sort, setSort] = useState(DEFAULT_SORT);
  const rows = useMemo(() => sortPortfolioRows(fin.rows, sort), [fin.rows, sort]);
  const t = fin.totals; const s = fin.summary;
  const toggle = (key) => setSort((cur) => cur.key === key ? { key, dir: cur.dir === "desc" ? "asc" : "desc" } : { key, dir: key === "name" ? "asc" : "desc" });
  const open = (id, el) => onOpenProject?.(id, el, "financials");
  const sortLabel = SORT_COLUMNS.find((c) => c.key === sort.key)?.label.toLowerCase();

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <h3 id="pfin-byproject" style={{ ...eyebrow, margin: 0, display: "inline-flex", alignItems: "center", color: T.projectsHubNavy }}>
          By project
          <Tip label="About the project table" testId="pfin-table-info" align="left" content="Select a project to open its Financials tab. Unknown values sort last in either direction; zero is a recorded value." />
        </h3>
        <span data-testid="pfin-sort-caption" style={{ fontSize: T.fsXs, color: T.mid }}>Sorted by {sortLabel} {sort.dir === "desc" ? "descending" : "ascending"}</span>
      </div>
      <ProjectsTableScroll testId="projects-financials-scroll">
        <table data-testid="projects-financials-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
          <thead>
            <tr>
              {SORT_COLUMNS.map((c) => {
                const active = sort.key === c.key;
                return (
                  <th key={c.key} scope="col" aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                    style={{ ...eyebrow, padding: "6px 8px", textAlign: c.numeric ? "right" : "left", borderBottom: `1px solid ${T.borderDark}`, whiteSpace: "nowrap", color: active ? T.projectsHubNavy : T.mid }}>
                    <button type="button" className="pfin-sort" data-testid={`pfin-sort-${c.key}`} onClick={() => toggle(c.key)}>
                      {c.label}<span aria-hidden="true" style={{ marginLeft: 4, opacity: active ? 1 : 0.3 }}>{active && sort.dir === "asc" ? "▲" : "▼"}</span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const href = projectHref?.(r.id);
              const muted = !r.tracked;
              return (
                <tr key={r.id} data-testid={`fin-row-${r.id}`} className="pfin-tr"
                  onClick={(e) => {
                    if (!onOpenProject || isModified(e) || e.target.closest?.("a,button,[role='button']")) return;
                    open(r.id, e.currentTarget.querySelector("a,button") || e.currentTarget);
                  }}>
                  <td style={{ ...td, textAlign: "left", fontWeight: T.fwSemibold, color: muted ? T.mid : T.dark }}>
                    {href ? (
                      <a href={href} className="pfin-link" data-testid={`pfin-link-${r.id}`} style={{ color: muted ? T.mid : undefined }}
                        onClick={(e) => {
                          if (!onOpenProject || isModified(e)) return;
                          e.preventDefault(); e.stopPropagation();
                          open(r.id, e.currentTarget);
                        }}>{r.name}</a>
                    ) : onOpenProject ? (
                      <button type="button" className="pfin-sort pfin-link" data-testid={`pfin-link-${r.id}`} style={{ fontWeight: T.fwSemibold, textTransform: "none", letterSpacing: 0 }}
                        onClick={(e) => { e.stopPropagation(); open(r.id, e.currentTarget); }}>{r.name}</button>
                    ) : r.name}
                    {muted && <span data-testid={`pfin-untracked-${r.id}`} style={{ marginLeft: 6, fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: T.mid, background: T.lightGray, borderRadius: 5, padding: "1px 6px" }}>Not tracked</span>}
                  </td>
                  {muted ? SORT_COLUMNS.slice(1).map((c) => <td key={c.key} data-testid={`fin-${c.key}-${r.id}`} style={td}>{DASH}</td>) : (
                    <>
                      <td style={td} data-testid={`fin-budget-${r.id}`}><Money value={r.budget} reason={r.issues.budget} testId={`pfin-budget-${r.id}`} /></td>
                      <td style={td} data-testid={`fin-forecast-${r.id}`}><Money value={r.forecast} reason={r.issues.forecast} testId={`pfin-forecast-${r.id}`} /></td>
                      <td style={td} data-testid={`fin-variance-${r.id}`}><Money value={r.variance} signed weight={T.fwBold} color={varColor(r.budget, r.forecast)} reason={r.issues.variance} testId={`pfin-variance-${r.id}`} /><br /><BudgetHealthLabel budget={r.budget} forecast={r.forecast} /></td>
                      <td style={td} data-testid={`fin-spent-${r.id}`}><Money value={r.spent} reason={r.issues.spendToDate} testId={`pfin-spent-${r.id}`} /></td>
                      <td style={td}><PortfolioRatio r={r.spentPct} testId={`fin-spentPct-${r.id}`} /></td>
                      <td style={td}><PortfolioRatio r={r.forecastVsBudget} overIsRed testId={`fin-forecastVsBudget-${r.id}`} /></td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr data-testid="fin-totals" style={{ background: T.blueSoft, borderTop: `2px solid ${T.projectsHubNavy}` }}>
              <td style={{ ...td, textAlign: "left", fontWeight: T.fwHubTitle, color: T.projectsHubNavy, borderBottom: "none" }}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <span data-testid="fin-tracking-caption">Portfolio total · {fin.trackedCount} tracked</span>
                  <Tip label="About portfolio totals" testId="pfin-total-info" align="left"
                    content={[coverageCaption, ...["budget", "forecast", "variance", "spent"].map((k) => `${k === "spent" ? "Actuals" : k[0].toUpperCase() + k.slice(1)}: ${coverageReason(k, t[k])}`)].filter(Boolean).join("\n")} />
                </span>
              </td>
              {["budget", "forecast", "variance", "spent"].map((k) => (
                <td key={k} data-testid={`fin-total-${k}`} style={{ ...td, borderBottom: "none" }}>
                  <Money value={s[k]} signed={k === "variance"} weight={T.fwHubTitle} color={k === "variance" ? varColor(s.budget, s.forecast) : T.projectsHubNavy}
                    reason={coverageReason(k, t[k])} testId={`pfin-total-${k}`} />
                  <div data-testid={`fin-coverage-${k}`} style={{ fontSize: T.fsXxs, color: T.mid, fontWeight: T.fwSemibold }}>{t[k].covered} of {t[k].total}{t[k].covered < t[k].total ? " · incomplete" : ""}</div>
                </td>
              ))}
              <td style={{ ...td, borderBottom: "none" }}><PortfolioRatio r={s.spentPct} testId="fin-total-spentPct" /></td>
              <td style={{ ...td, borderBottom: "none" }}><PortfolioRatio r={s.forecastVsBudget} overIsRed testId="fin-total-forecastVsBudget" /></td>
            </tr>
          </tfoot>
        </table>
      </ProjectsTableScroll>
    </>
  );
}
